// ============================================================================
// useNotificationInbox HOOK
// ============================================================================
// Drives the bell-icon panel in main_layout. Pulls the current user's in-app
// notifications from the CRS proxy (`/api/v1/notifications/inbox`), polls for
// new items at a configurable interval, and exposes mark-read / archive
// actions that re-sync state on success.

import { useCallback, useEffect, useRef, useState } from 'react';
import { notificationInboxService } from '../../api/services/notificationInboxService';
import type { ID, InAppNotification } from '../../api/types';

interface UseNotificationInboxOptions {
  /** When false the hook does nothing — caller is not authenticated yet. */
  enabled?: boolean;
  /** How many items to fetch per call. Defaults to 50. */
  pageSize?: number;
  /** Background poll interval in ms. Set to 0 to disable. Defaults to 30s. */
  pollIntervalMs?: number;
}

export interface UseNotificationInboxResult {
  notifications: InAppNotification[];
  unreadCount: number;
  loading: boolean;
  error: Error | null;
  /** True if the current user's role is in notifications.allowed_roles.
   *  When false, polling does not run and the bell should stay hidden. */
  hasAccess: boolean;
  /** Force-refresh the inbox + unread count. */
  refresh: () => Promise<void>;
  /** Mark one notification as read (idempotent server-side). */
  markRead: (channelId: ID) => Promise<void>;
  /** Mark every notification in the inbox as read. */
  markAllRead: () => Promise<void>;
  /** Archive (hide from the inbox view). */
  archive: (channelId: ID) => Promise<void>;
}

const DEFAULT_PAGE_SIZE = 50;
const DEFAULT_POLL_MS = 30_000;

export function useNotificationInbox(
  options: UseNotificationInboxOptions = {},
): UseNotificationInboxResult {
  const {
    enabled = true,
    pageSize = DEFAULT_PAGE_SIZE,
    pollIntervalMs = DEFAULT_POLL_MS,
  } = options;

  const [notifications, setNotifications] = useState<InAppNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [hasAccess, setHasAccess] = useState(false);
  const inflightRef = useRef(false);

  // Resolve access (role gate) once when the hook becomes enabled.
  // If the user's role isn't in notifications.allowed_roles, polling never
  // starts and the bell stays hidden.
  useEffect(() => {
    if (!enabled) {
      setHasAccess(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const allowed = await notificationInboxService.getAccess();
        if (!cancelled) setHasAccess(allowed);
      } catch (e) {
        if (!cancelled) {
          setHasAccess(false);
          setError(e instanceof Error ? e : new Error(String(e)));
        }
      }
    })();
    return () => { cancelled = true; };
  }, [enabled]);

  const fetchInbox = useCallback(async () => {
    if (!enabled || !hasAccess || inflightRef.current) return;
    inflightRef.current = true;
    setLoading(true);
    try {
      const [inbox, unread] = await Promise.all([
        notificationInboxService.getInbox({ page: 1, pageSize }),
        notificationInboxService.getUnreadCount(),
      ]);
      setNotifications(inbox.items);
      setUnreadCount(unread);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e : new Error(String(e)));
    } finally {
      setLoading(false);
      inflightRef.current = false;
    }
  }, [enabled, hasAccess, pageSize]);

  // Initial load + polling (gated on access)
  useEffect(() => {
    if (!enabled || !hasAccess) return;
    fetchInbox();
    if (pollIntervalMs <= 0) return;
    const handle = window.setInterval(fetchInbox, pollIntervalMs);
    return () => window.clearInterval(handle);
  }, [enabled, hasAccess, pollIntervalMs, fetchInbox]);

  const markRead = useCallback(
    async (channelId: ID) => {
      try {
        await notificationInboxService.markRead({ channelIds: [channelId] });
        // Optimistic — drop the unread flag locally; full refresh on next tick.
        setNotifications((prev) =>
          prev.map((n) =>
            n.channelId === channelId ? { ...n, isRead: true, readAt: new Date().toISOString() } : n,
          ),
        );
        setUnreadCount((c) => Math.max(0, c - 1));
      } catch (e) {
        setError(e instanceof Error ? e : new Error(String(e)));
      }
    },
    [],
  );

  const markAllRead = useCallback(async () => {
    try {
      await notificationInboxService.markRead({ markAllRead: true });
      const now = new Date().toISOString();
      setNotifications((prev) =>
        prev.map((n) => (n.isRead ? n : { ...n, isRead: true, readAt: now })),
      );
      setUnreadCount(0);
    } catch (e) {
      setError(e instanceof Error ? e : new Error(String(e)));
    }
  }, []);

  const archive = useCallback(
    async (channelId: ID) => {
      try {
        await notificationInboxService.archive(channelId);
        setNotifications((prev) => {
          const removed = prev.find((n) => n.channelId === channelId);
          if (removed && !removed.isRead) {
            setUnreadCount((c) => Math.max(0, c - 1));
          }
          return prev.filter((n) => n.channelId !== channelId);
        });
      } catch (e) {
        setError(e instanceof Error ? e : new Error(String(e)));
      }
    },
    [],
  );

  return {
    notifications,
    unreadCount,
    loading,
    error,
    hasAccess,
    refresh: fetchInbox,
    markRead,
    markAllRead,
    archive,
  };
}

export default useNotificationInbox;
